import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ArcaPageShell from './components/ArcaPageShell';
import { deleteArcaModelo, loadArcaModelos } from './utils/arcaStorage';

function ModeloCard({ modelo, onOpen, onDelete }) {
  const preview = modelo.slides?.[0];
  const chartCount = (modelo.slides || []).reduce(
    (n, s) => n + (s.graficos?.length || 0),
    0
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': { borderColor: '#94a3b8', boxShadow: '0 4px 20px rgba(15,23,42,0.08)' },
      }}
    >
      <Box
        sx={{
          aspectRatio: '16 / 9',
          bgcolor: '#0f172a',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: '#64748b', mb: 0.5 }}>
          {modelo.slideCount} slide(s)
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ color: '#f8fafc', fontWeight: 700, lineHeight: 1.3 }}
          noWrap
        >
          {preview?.titulo || modelo.nome}
        </Typography>
        {preview?.subtitulo && (
          <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5 }} noWrap>
            {preview.subtitulo}
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {modelo.nome}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label={`${modelo.slideCount} slides`} size="small" variant="outlined" />
          {chartCount > 0 && (
            <Chip label={`${chartCount} gráficos`} size="small" color="primary" variant="outlined" />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
          Atualizado {new Date(modelo.atualizadoEm).toLocaleString('pt-BR')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => onOpen(modelo)}
            sx={{ textTransform: 'none', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
          >
            Abrir
          </Button>
          <Tooltip title="Excluir modelo">
            <IconButton size="small" onClick={() => onDelete(modelo.id)} sx={{ color: '#dc2626' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}

export default function ArcaModelosPage() {
  const navigate = useNavigate();
  const [modelos, setModelos] = useState(() => loadArcaModelos());
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const refresh = () => setModelos(loadArcaModelos());

  const handleOpen = useCallback(
    (modelo) => {
      navigate('/arca/apresentacao/workspace', { state: { loadModelo: modelo } });
    },
    [navigate]
  );

  const handleDelete = useCallback((id) => {
    deleteArcaModelo(id);
    refresh();
    setSnack({ open: true, message: 'Modelo excluído.', severity: 'success' });
  }, []);

  return (
    <ArcaPageShell
      title="Modelos Salvos"
      subtitle="Apresentações guardadas no navegador. Abra para continuar editando ou duplique a partir do workspace."
    >
      {modelos.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '2px dashed #cbd5e1',
            borderRadius: 2,
            bgcolor: '#fff',
          }}
        >
          <FolderCopyOutlinedIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Nenhum modelo salvo ainda
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 420, mx: 'auto' }}>
            No workspace de Apresentações, use o botão «Salvar modelo» na barra superior para guardar o deck
            atual aqui.
          </Typography>
          <Button
            variant="contained"
            startIcon={<SlideshowIcon />}
            onClick={() => navigate('/arca/apresentacao/workspace')}
            sx={{ textTransform: 'none', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
          >
            Ir para Apresentações
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {modelos.map((modelo) => (
            <Grid item xs={12} sm={6} md={4} key={modelo.id}>
              <ModeloCard modelo={modelo} onOpen={handleOpen} onDelete={handleDelete} />
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </ArcaPageShell>
  );
}
